import React, { MouseEvent } from 'react';
import { TextField, Typography, Button, darken, styled, Grid } from '@mui/material';
import { useApp } from 'hooks/app';
import { AccountValidation } from './Account';
import { User } from 'types/user';
import CpfInput from 'components/masked-input/CpfInput';
import KeyboardDatePicker from 'components/pickers/KeyboardDatePicker';
import PhoneInput from 'components/masked-input/PhoneInput';
import { asInputComponent } from 'components/masked-input/asInputComponent';

const Container = styled(Grid)({
  margin: '20px 0 30px',
  maxWidth: 720,
});

const Actions = styled('div')({
  '& button': {
    marginTop: 20,
  },
  '& a': {
    fontWeight: 'normal',
  },
});

const DisconnectButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.error.main,
  width: '100%',
  color: 'white',
  height: 40,
  '&:hover': {
    backgroundColor: darken(theme.palette.error.main, 0.1),
  },
}));

const SaveButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.success.main,
  width: '100%',
  color: 'white',
  height: 40,
  '&:hover': {
    backgroundColor: darken(theme.palette.success.main, 0.1),
  },
}));

type AccountFormProps = {
  localUser: User;
  handleChange(index: string, value: any): void;
  handleValidation(): void;
  handleResetEmail(): void;
  validation: AccountValidation;
};

const AccountForm: React.FC<AccountFormProps> = ({
  localUser,
  handleValidation,
  handleChange,
  handleResetEmail,
  validation,
}) => {
  const { handleLogout } = useApp();

  function handleDisconnectClick() {
    handleLogout();
  }

  function handleResetEmailClick(e: MouseEvent<HTMLAnchorElement>) {
    e.preventDefault();
    handleResetEmail();
  }

  return (
    <Container container spacing={1}>
      <Grid container size={{ md: 8, xs: 12 }}>
        <Typography fontWeight={600} variant="body2">
          Informações de Login
        </Typography>

        <Grid size={12}>
          <TextField
            error={!!validation.username}
            helperText={validation.username}
            label="Nome de usuário"
            placeholder="Digite o nome de usuário"
            value={localUser.username}
            disabled
            fullWidth
            margin="normal"
          />
        </Grid>

        <Grid size={12}>
          <TextField
            error={!!validation.email}
            helperText={validation.email}
            label="E-mail"
            placeholder="Digite o e-mail"
            value={localUser.email}
            disabled
            fullWidth
            margin="normal"
          />
        </Grid>

        <Grid size={12}>
          <Typography fontWeight={600} variant="body2">
            Informações do usuário
          </Typography>
        </Grid>

        <Grid container size={12}>
          <TextField
            error={!!validation.name}
            helperText={validation.name}
            label="Nome"
            placeholder="Digite o nome completo"
            value={localUser.name}
            fullWidth
            margin="normal"
            autoFocus
            onChange={e => handleChange('name', e.target.value)}
          />

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              error={!!validation.rg}
              helperText={validation.rg}
              label="RG"
              placeholder="Digite o RG"
              value={localUser.rg}
              fullWidth
              margin="normal"
              autoFocus
              onChange={e => handleChange('rg', e.target.value)}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              error={!!validation.document_number}
              helperText={validation.document_number}
              label="CPF"
              placeholder="Digite o CPF"
              value={localUser.document_number}
              fullWidth
              margin="normal"
              onChange={e => handleChange('document_number', e.target.value)}
              slotProps={{
                input: {
                  inputComponent: asInputComponent(CpfInput),
                },
              }}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label="Telefone"
              placeholder="Digite o telefone"
              value={localUser.phone}
              fullWidth
              margin="normal"
              onChange={e => handleChange('phone', e.target.value)}
              slotProps={{
                input: {
                  inputComponent: asInputComponent(PhoneInput),
                },
              }}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <KeyboardDatePicker
              disableFuture
              margin="normal"
              label="Data de nascimento"
              value={localUser.birth_date}
              onChange={date => handleChange('birth_date', date)}
            />
          </Grid>
        </Grid>

        <Grid size={12}>
          <Typography fontWeight={600} variant="body2">
            Endereço
          </Typography>
        </Grid>

        <Grid size={12}>
          <TextField
            label="Endereço"
            placeholder="Digite o endereço completo"
            multiline
            rows={2}
            value={localUser.address}
            fullWidth
            margin="normal"
            autoFocus
            onChange={e => handleChange('address', e.target.value)}
          />
        </Grid>
      </Grid>

      <Grid size={{ md: 6, xs: 12 }}>
        <Actions>
          <a href="/" onClick={handleResetEmailClick}>
            Alterar minha senha
          </a>
          <Typography variant="body2" color="textSecondary">
            Será enviado uma mensagem de e-mail com opção para alterar a senha
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <DisconnectButton onClick={handleDisconnectClick}>Desconectar</DisconnectButton>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <SaveButton onClick={handleValidation}>Salvar</SaveButton>
            </Grid>
          </Grid>
        </Actions>
      </Grid>
    </Container>
  );
};

export default AccountForm;
